import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Coins, ShoppingBag, ArrowRight, Loader2, Film, ArrowUpRight, ArrowDownRight,
  LayoutDashboard, Plus, MessageCircle, Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { getCreditBalance, listSalesVideos, getCreditTransactions } from "@/lib/api";
import { statusGroup, statusLabel, TONE_CLASS, dashboardCounts, salesVideoProjectPath } from "@/lib/salesVideoStatus";
import { waLink, BUSINESS } from "@/lib/site";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import RecentActivity from "@/components/RecentActivity";

const TYPE_LABEL = {
  credit: "Kredit Masuk", debit: "Pemakaian", reserve: "Reservasi",
  release: "Pelepasan", refund: "Pengembalian", adjustment: "Penyesuaian",
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
};

const snippet = (text, n = 90) => {
  if (!text) return "Tanpa deskripsi";
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length > n ? `${clean.slice(0, n)}…` : clean;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [videos, setVideos] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getCreditBalance().catch(() => ({ credit_balance: null })),
      listSalesVideos().catch(() => ({ sales_videos: [] })),
      getCreditTransactions().catch(() => ({ transactions: [] })),
    ])
      .then(([b, v, t]) => {
        setBalance(b.credit_balance);
        setVideos(v.sales_videos || []);
        setTxns(t.transactions || []);
      })
      .catch(() => toast.error("Gagal memuat dashboard"))
      .finally(() => setLoading(false));
  }, []);

  const counts = dashboardCounts(videos);

  const summary = [
    { key: "draft", label: "Draft", tone: "slate" },
    { key: "processing", label: "Memproses", tone: "blue" },
    { key: "success", label: "Berhasil", tone: "emerald" },
    { key: "failed", label: "Gagal", tone: "red" },
    { key: "review", label: "Perlu diperiksa", tone: "amber" },
  ];

  const recentVideos = videos.slice(0, 6);
  const recentTxns = txns.slice(0, 5);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-8 p-4 md:p-8" data-testid="member-dashboard">
      {/* Greeting */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white">
          <LayoutDashboard className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Dashboard</p>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 md:text-3xl" data-testid="dashboard-greeting">
            Halo, {user?.name || "Pengguna"} 👋
          </h1>
        </div>
      </div>

      {/* Credit + CTA */}
      <div className="grid gap-6 md:grid-cols-[1fr_1.2fr]">
        <div className="rounded-2xl bg-blue-600 p-7 text-white" data-testid="dashboard-credit-card">
          <div className="flex items-center gap-2 text-blue-100">
            <Coins className="h-5 w-5" /> Saldo Kredit
          </div>
          <p className="mt-2 font-heading text-4xl font-bold" data-testid="dashboard-credit-value">
            {balance === null ? "—" : balance} <span className="text-xl font-medium text-blue-100">Kredit</span>
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <Link to="/beli-kredit" data-testid="dashboard-buy-credit" className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50">
              <Wallet className="h-4 w-4" /> Beli Kredit
            </Link>
            <Link to="/kredit" data-testid="dashboard-credit-link" className="inline-flex items-center gap-1 text-sm font-medium text-blue-100 hover:text-white">
              Riwayat kredit <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-7">
          <div>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <ShoppingBag className="h-5 w-5" />
            </div>
            <h2 className="mt-4 font-heading text-lg font-semibold text-slate-900">Video Jualan AI</h2>
            <p className="mt-1 text-sm text-slate-600">Mulai dari foto produk, lalu AI bantu buat videonya.</p>
          </div>
          <Link to="/video-jualan" data-testid="dashboard-create-video" className="mt-5">
            <Button className="w-full rounded-full bg-blue-600 text-white hover:bg-blue-700 active:scale-95 transition-transform">
              <Plus className="mr-2 h-4 w-4" /> Buat Video Jualan
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="dashboard-shortdrama">
        <div><h2 className="font-heading text-lg font-semibold text-slate-900">ShortDrama Studio</h2><p className="mt-1 text-sm text-slate-600">Bangun cerita pendek untuk produk, jasa, bisnis, atau penawaran Anda.</p></div>
        <Link to="/shortdrama"><Button className="bg-blue-600 hover:bg-blue-700"><Film className="mr-2 h-4 w-4" />Buat ShortDrama</Button></Link>
      </div>

      <OnboardingChecklist balance={balance} videos={videos} counts={counts} ready={!loading} />

      {/* Status summary */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5" data-testid="dashboard-status-summary">
        {summary.map((s) => (
          <div key={s.key} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`summary-${s.key}`}>
            <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE_CLASS[s.tone]}`}>{s.label}</span>
            <p className="mt-3 font-heading text-3xl font-bold text-slate-900" data-testid={`summary-count-${s.key}`}>{counts[s.key] || 0}</p>
          </div>
        ))}
      </div>

      <RecentActivity />

      {/* Latest videos */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Video Terbaru</h2>
          <Link to="/riwayat" data-testid="dashboard-view-history" className="text-sm font-medium text-blue-600 hover:underline">Lihat semua</Link>
        </div>
        {recentVideos.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="dashboard-videos-empty">
            <Film className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-heading font-semibold text-slate-900">Belum ada video.</p>
            <p className="mt-1 text-sm text-slate-500">Buat video jualan pertama Anda dari foto produk.</p>
            <Link to="/video-jualan" data-testid="dashboard-empty-cta" className="mt-4 inline-block">
              <Button className="rounded-full bg-blue-600 text-white hover:bg-blue-700">Buat Video Jualan</Button>
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="dashboard-video-list">
            {recentVideos.map((v) => {
              const g = statusGroup(v);
              return (
                <Link
                  key={v.id}
                  to={salesVideoProjectPath(v.id)}
                  data-testid={`dashboard-video-${v.id}`}
                  className="group rounded-xl border border-slate-200 bg-white p-5 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                      <Film className="h-4 w-4" />
                    </span>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE_CLASS[g.tone]}`} data-testid={`dashboard-video-status-${v.id}`}>
                      {statusLabel(v)}
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-slate-700">{snippet(v.product_description)}</p>
                  <p className="mt-2 text-xs text-slate-400">{formatDate(v.created_at)}</p>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent credit usage */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Riwayat Kredit Terbaru</h2>
          <Link to="/kredit" data-testid="dashboard-view-credits" className="text-sm font-medium text-blue-600 hover:underline">Lihat semua</Link>
        </div>
        {recentTxns.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="dashboard-credits-empty">
            Belum ada transaksi kredit.
          </div>
        ) : (
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white" data-testid="dashboard-credit-list">
            {recentTxns.map((t) => {
              const positive = t.amount >= 0;
              return (
                <div key={t.id} className="flex items-center gap-4 px-5 py-4">
                  <span className={`flex h-9 w-9 items-center justify-center rounded-full ${positive ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                    {positive ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900">{TYPE_LABEL[t.type] || t.type}</p>
                    <p className="text-xs text-slate-400">{formatDate(t.created_at)} · {t.status}</p>
                  </div>
                  <span className={`font-semibold ${positive ? "text-emerald-600" : "text-amber-600"}`}>{positive ? "+" : ""}{t.amount}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Help CTA */}
      <div className="flex flex-col items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:flex-row sm:items-center" data-testid="dashboard-help">
        <div>
          <p className="font-heading font-semibold text-slate-900">Butuh bantuan?</p>
          <p className="mt-1 text-sm text-slate-500">Tim kami siap membantu via WhatsApp {BUSINESS.whatsappDisplay}.</p>
        </div>
        <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="dashboard-whatsapp">
          <Button className="rounded-full bg-[#25D366] text-white hover:bg-[#1ebe5b] active:scale-95 transition-transform">
            <MessageCircle className="mr-2 h-4 w-4" /> Chat WhatsApp Admin
          </Button>
        </a>
      </div>
    </div>
  );
}
